import { GridLogicOperator, GridFilterModel as MuiGridFilterModel, GridSortModel as MuiGridSortModel } from '@mui/x-data-grid'
import { AssertNeverError, AvailableLocale } from '@omnea-digital/toolbox'
import { GridFilterItem, GridFilterModel, GridFilterItemSchema } from '@omnea-app/packages/universal/tables/gridModel'

interface TransformFilterModelOptions {
  filterModel?: MuiGridFilterModel
  filterNullValues?: boolean
  locale?: AvailableLocale
}
interface TransformSortModelOptions {
  sortModel?: MuiGridSortModel
}

// Transforms MUI's data grid model into our own model
// This function includes safe parsing so we have expected (operator, valueTypes) pairs for backend filtering
export function transformMuiFilterModelToOmnea({
  filterModel,
  filterNullValues = false,
  locale,
}: TransformFilterModelOptions): GridFilterModel | undefined {
  if (filterModel == null) {
    return undefined
  }

  const items = filterModel.items
  if (items.length === 0 && !filterModel.quickFilterValues) {
    return undefined
  }

  const processedItems = filterNullValues
    ? items.filter(
        (item) =>
          item.operator === 'isEmpty' || item.operator === 'isNotEmpty' || (item.value !== null && item.value !== undefined)
      )
    : items

  const transformedItems = processedItems.flatMap((item) => {
    const parsed = GridFilterItemSchema.safeParse(item)

    if (!parsed.success) {
      console.error('Failed to parse filter item:', parsed.error)
      return []
    }

    return [parsed.data]
  })

  if (transformedItems.length === 0 && !filterModel.quickFilterValues) {
    return undefined
  }

  const enrichedItems = locale ? transformedItems.map((item) => enrichFilterItemWithLocale(item, locale)) : transformedItems

  const logicOperator = filterModel?.logicOperator
  return {
    items: enrichedItems,
    ...(logicOperator === GridLogicOperator.And
      ? { logicOperator: 'and' }
      : logicOperator === GridLogicOperator.Or
        ? { logicOperator: 'or' }
        : {}),
    quickFilterValues: filterModel?.quickFilterValues,
  }
}

const enrichFilterItemWithLocale = (item: GridFilterItem, locale: AvailableLocale): GridFilterItem => {
  if (item.field === 'address' && item.operator === 'contains') {
    return { ...item, context: { locale } }
  }
  return item
}

export function transformMuiSortModelToOmnea({ sortModel }: TransformSortModelOptions) {
  if (!sortModel || sortModel.length === 0) {
    return undefined
  }

  return sortModel
    .filter((item) => item.sort !== null)
    .map((item) => ({
      field: item.field,
      sort: item.sort as 'asc' | 'desc',
    }))
}

export function transformOmneaFilterModelToMui(filterModel?: GridFilterModel): MuiGridFilterModel {
  if (!filterModel) {
    return { items: [] }
  }

  const { items, logicOperator, quickFilterValues } = filterModel

  return {
    items,
    ...(logicOperator ? { logicOperator: convertLogicOperatorToMui(logicOperator) } : {}),
    quickFilterValues,
  }
}

const convertLogicOperatorToMui = (logicOperator: 'and' | 'or'): GridLogicOperator => {
  if (logicOperator === 'and') {
    return GridLogicOperator.And
  }
  if (logicOperator === 'or') {
    return GridLogicOperator.Or
  }
  throw new AssertNeverError(logicOperator)
}
